import { EDGE_COUNT, type LightFrameAudio } from "./lightFrameEngine";
import { mixFrameFrequencyBands } from "./lightFrameResponse";
export { frameFrequencyMix } from "./lightFrameResponse";
export interface LightFrameFrequencyRange {
  minFreq: number; maxFreq: number;
  bassMin: number; bassMax: number;
  highMin: number; highMax: number;
}
export function readLightFrameAudio(values: number[], range: LightFrameFrequencyRange, framePositions?: readonly number[], frequencies?: readonly number[]): LightFrameAudio {
  const bands = new Array<number>(EDGE_COUNT).fill(0);
  const midBands = new Array<number>(EDGE_COUNT).fill(0);
  const highBands = new Array<number>(EDGE_COUNT).fill(0);
  const counts = new Array<number>(EDGE_COUNT).fill(0);
  const lowBands = [0, 0, 0, 0], lowCounts = [0, 0, 0, 0];
  let bass = 0, bassCount = 0, treble = 0, trebleCount = 0;
  for (let i = 0; i < values.length; i++) {
    const value = values[i]!;
    const energy = Number.isFinite(value) ? Math.max(0, Math.min(1, (value - 0.5) * 2)) : 0;
    const progress = values.length > 1 ? i / (values.length - 1) : 0;
    const frequency = frequencies?.[i] ?? range.minFreq * (range.maxFreq / range.minFreq) ** progress;
    const band = Math.min(EDGE_COUNT - 1, Math.floor(progress * EDGE_COUNT));
    // Permutation spreads low/mid/high frequencies around different sides and depths.
    const edge = (band * 13) % EDGE_COUNT;
    bands[edge]! += energy;
    counts[edge]! += 1;
    const isBass = frequency >= range.bassMin && frequency <= range.bassMax;
    const isHigh = frequency >= range.highMin && frequency <= range.highMax;
    if (isBass) {
      bass += energy; bassCount++;
      const lowProgress = Math.log(frequency / range.bassMin) / Math.log(range.bassMax / range.bassMin);
      const lowSide = Math.min(3, Math.max(0, Math.floor(lowProgress * 4)));
      lowBands[lowSide]! += energy; lowCounts[lowSide]!++;
    }
    if (isHigh) { treble += energy; trebleCount++; }
    if (!isBass) {
      if (isHigh) highBands[edge]! += energy;
      else midBands[edge]! += energy;
    }
  }
  for (let i = 0; i < EDGE_COUNT; i++) {
    bands[i] = counts[i] ? bands[i]! / counts[i]! : 0;
    midBands[i] = counts[i] ? midBands[i]! / counts[i]! : 0;
    highBands[i] = counts[i] ? highBands[i]! / counts[i]! : 0;
  }
  const frequencyBands = {
    bass: Array.from({ length: EDGE_COUNT }, (_, edge) => {
      const side = edge % 4;
      return lowCounts[side] ? lowBands[side]! / lowCounts[side]! : bassCount ? bass / bassCount : 0;
    }),
    mid: midBands, high: highBands,
    rangeKey: `${range.bassMin}:${range.bassMax}:${range.highMin}:${range.highMax}`,
  };
  return { bands: framePositions ? mixFrameFrequencyBands(frequencyBands, framePositions) : bands,
    frequencyBands, bass: bassCount ? bass / bassCount : 0, treble: trebleCount ? treble / trebleCount : 0 };
}
